/**
 * Shockwave Bloom Visualizer - Radial shockwave rings with glowing edges and particle bursts
 *
 * Large radial rings emanate from center (or random positions on beat) with bloom halos
 * and particle bursts. Uses additive blending for glow accumulation and semi-transparent
 * overlay for trail fade persistence.
 *
 * Visual layers (drawn under additive composition):
 * 1. Breathing vignette — radial gradient darkening edges, inner radius breathes with bass
 * 2. Shockwave rings — expanding arcs with layered bloom halo strokes
 * 3. Burst particles — radial particle spray from each ring spawn point
 *
 * Performance notes:
 * - Object pools (rings + particles) with free-list allocation for zero-GC animation
 * - Bloom halo via 3 concentric arc strokes at lower alpha (no shadowBlur, no getImageData)
 * - Vignette gradient cached and rebuilt only on quantized bass change or canvas resize
 * - Trail fill style string cached, rebuilt only on config change
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

// Metadata for manifest generation
export const metadata = {
  name: 'shockwave-bloom',
  displayName: 'Shockwave Bloom',
  contextType: '2d',
};

// Object states
const DEAD = 0;
const ALIVE = 1;

// Pool limits
const MAX_RINGS = 30;
const MAX_PARTICLES = 1500;

class ShockwaveBloom extends Base2DVisualizer {
  static name = 'shockwave-bloom';
  static displayName = 'Shockwave Bloom';

  static getConfigUI() {
    return [
      { type: 'range', id: 'ringRate', label: 'Ring Rate', min: 1, max: 10, default: 4 },
      { type: 'range', id: 'ringSpeed', label: 'Ring Speed', min: 1, max: 15, default: 5 },
      { type: 'range', id: 'ringThickness', label: 'Ring Thickness', min: 2, max: 30, default: 8 },
      { type: 'range', id: 'ringLife', label: 'Ring Life', min: 30, max: 200, default: 90 },
      { type: 'range', id: 'burstParticles', label: 'Burst Particles', min: 5, max: 60, default: 25 },
      { type: 'range', id: 'particleSpeed', label: 'Particle Speed', min: 1, max: 12, default: 5 },
      { type: 'range', id: 'particleDrag', label: 'Particle Drag', min: 90, max: 99, default: 96, scale: 0.01 },
      {
        type: 'select', id: 'centerMode', label: 'Center Mode', default: 'fixed',
        options: [
          { value: 'fixed', label: 'Fixed Center' },
          { value: 'beatRandom', label: 'Beat Random' }
        ]
      },
      { type: 'range', id: 'glowIntensity', label: 'Glow Intensity', min: 0, max: 100, default: 60, scale: 0.01 },
      { type: 'range', id: 'trailAlpha', label: 'Trail Fade', min: 3, max: 30, default: 10, scale: 0.01 },
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 0, max: 95, default: 50, scale: 0.01 },
      { type: 'color', id: 'colorStart', label: 'Color Start', default: '#00ccff' },
      { type: 'color', id: 'colorEnd', label: 'Color End', default: '#ff00cc' },
      { type: 'color', id: 'backgroundColor', label: 'Background', default: '#050510' },
    ];
  }

  init() {
    // --- Ring pool ---
    this.rings = new Array(MAX_RINGS);
    this.ringFreeList = new Array(MAX_RINGS);
    this.ringFreeCount = MAX_RINGS;

    for (let i = 0; i < MAX_RINGS; i++) {
      this.rings[i] = {
        x: 0, y: 0,
        radius: 0, speed: 0,
        life: 0, maxLife: 0,
        thickness: 0,
        hue: 0,
        state: DEAD
      };
      this.ringFreeList[i] = i;
    }

    // --- Particle pool ---
    this.particles = new Array(MAX_PARTICLES);
    this.particleFreeList = new Array(MAX_PARTICLES);
    this.particleFreeCount = MAX_PARTICLES;

    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.particles[i] = {
        x: 0, y: 0,
        vx: 0, vy: 0,
        life: 0,
        size: 0,
        hue: 0,
        drag: 0.96,
        state: DEAD
      };
      this.particleFreeList[i] = i;
    }

    // Spawn state
    this.lastSpawnTime = 0;
    this.hueRotation = 0;
    this.isFirstFrame = true;

    // Cache parsed background RGB and trail fill style
    this._bgR = 5;
    this._bgG = 5;
    this._bgB = 16;
    this._trailFillStyle = '';
    this._updateTrailFillStyle();

    // Vignette gradient cache
    this._vignetteGrad = null;
    this._vignetteW = 0;
    this._vignetteH = 0;
    this._vignetteBassQ = -1;

    // Parse colorStart / colorEnd into HSL hue values
    this._startHue = 0;
    this._endHue = 0;
    this._updateColorHues();
  }

  onConfigChange(changed) {
    if (changed.backgroundColor !== undefined || changed.trailAlpha !== undefined) {
      this._updateTrailFillStyle();
    }
    if (changed.colorStart !== undefined || changed.colorEnd !== undefined) {
      this._updateColorHues();
    }
  }

  getElementCount() {
    return 8;
  }

  // --- Pool allocation helpers ---

  allocRing() {
    if (this.ringFreeCount === 0) return -1;
    return this.ringFreeList[--this.ringFreeCount];
  }

  freeRing(index) {
    if (this.rings[index].state === DEAD) return;
    this.rings[index].state = DEAD;
    this.ringFreeList[this.ringFreeCount++] = index;
  }

  allocParticle() {
    if (this.particleFreeCount === 0) return -1;
    return this.particleFreeList[--this.particleFreeCount];
  }

  freeParticle(index) {
    if (this.particles[index].state === DEAD) return;
    this.particles[index].state = DEAD;
    this.particleFreeList[this.particleFreeCount++] = index;
  }

  // --- Config helpers ---

  _updateTrailFillStyle() {
    const bg = this.config.backgroundColor || '#050510';
    this._bgR = parseInt(bg.slice(1, 3), 16) || 0;
    this._bgG = parseInt(bg.slice(3, 5), 16) || 0;
    this._bgB = parseInt(bg.slice(5, 7), 16) || 0;
    this._trailFillStyle = `rgba(${this._bgR}, ${this._bgG}, ${this._bgB}, ${this.config.trailAlpha})`;
  }

  _updateColorHues() {
    this._startHue = hexToHue(this.config.colorStart || '#00ccff');
    this._endHue = hexToHue(this.config.colorEnd || '#ff00cc');
  }

  /**
   * Compute a hue value for the current hue rotation, interpolating
   * between colorStart and colorEnd hues.
   */
  _currentHue() {
    // Blend the two configured hues, offset by continuous rotation
    const blend = (Math.sin(this.hueRotation * 0.02) + 1) / 2; // 0..1 oscillation
    const baseHue = this._startHue + (this._endHue - this._startHue) * blend;
    return ((baseHue + this.hueRotation) % 360 + 360) % 360;
  }

  // --- Spawning ---

  _spawnRing(x, y) {
    const idx = this.allocRing();
    if (idx === -1) return;

    const { config } = this;
    const ring = this.rings[idx];

    ring.x = x;
    ring.y = y;
    ring.radius = 0;
    ring.speed = config.ringSpeed;
    ring.life = config.ringLife;
    ring.maxLife = config.ringLife;
    ring.thickness = config.ringThickness;
    ring.hue = this._currentHue();
    ring.state = ALIVE;

    // Spawn accompanying particle burst
    this._spawnBurst(x, y, ring.hue);
  }

  _spawnBurst(x, y, hue) {
    const { config } = this;
    const count = config.burstParticles;
    const speed = config.particleSpeed;
    const drag = config.particleDrag;
    const angleStep = (Math.PI * 2) / count;

    for (let i = 0; i < count; i++) {
      const idx = this.allocParticle();
      if (idx === -1) break;

      const angle = angleStep * i + (Math.random() - 0.5) * 0.4;
      const v = speed * (0.5 + Math.random() * 0.8);

      const p = this.particles[idx];
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * v;
      p.vy = Math.sin(angle) * v;
      p.life = 1;
      p.size = 1.5 + Math.random() * 2;
      // Vary hue slightly per particle for natural spread
      p.hue = (hue + (Math.random() - 0.5) * 40 + 360) % 360;
      p.drag = drag;
      p.state = ALIVE;
    }
  }

  // --- draw() override for trail fade ---

  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    // Ensure buffer is properly sized
    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }

    // Map frequency data to element count
    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    // Ensure previousAmplitudes is properly sized
    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    // Apply smoothing
    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);

    // Store features for convenience getters (bassEnergy, beatDetected, etc.)
    this._currentFeatures = features;

    // Trail effect: semi-transparent overlay instead of full clear
    const { ctx, canvas } = this;
    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      ctx.fillStyle = this._trailFillStyle;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    this.renderFrame(amplitudes, features);
  }

  // --- Main render ---

  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const now = performance.now();

    // Continuous hue rotation — slow drift
    this.hueRotation = (this.hueRotation + 0.5) % 360;

    // --- Spawn decision ---
    const cooldown = 1000 / config.ringRate;
    const bass = this.bassEnergy;
    const shouldSpawn =
      (this.beatDetected || bass > 0.8) &&
      (now - this.lastSpawnTime) > cooldown;

    if (shouldSpawn) {
      let sx, sy;
      if (config.centerMode === 'beatRandom') {
        // Random position within central 60% of canvas
        sx = canvas.width * (0.2 + Math.random() * 0.6);
        sy = canvas.height * (0.2 + Math.random() * 0.6);
      } else {
        sx = canvas.width / 2;
        sy = canvas.height / 2;
      }
      this._spawnRing(sx, sy);
      this.lastSpawnTime = now;
    }

    // --- Draw breathing vignette ---
    this._drawVignette(bass);

    // --- Switch to additive composition for glow accumulation ---
    // save/restore guards against state leakage if an exception occurs mid-render
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    // --- Update + draw rings ---
    const glowAlpha = config.glowIntensity;

    for (let i = 0; i < MAX_RINGS; i++) {
      const ring = this.rings[i];
      if (ring.state === DEAD) continue;

      // Expand and age
      ring.radius += ring.speed;
      ring.life--;

      if (ring.life <= 0) {
        this.freeRing(i);
        continue;
      }

      // Alpha fades over lifetime — smooth in, smooth out
      const lifeRatio = ring.life / ring.maxLife;
      // Fade in over first 15%, hold, fade out over last 30%
      let alpha;
      if (lifeRatio > 0.85) {
        alpha = (1 - lifeRatio) / 0.15; // fade in
      } else if (lifeRatio < 0.30) {
        alpha = lifeRatio / 0.30; // fade out
      } else {
        alpha = 1;
      }

      const hue = ring.hue;
      const sat = 90;
      const light = 55 + alpha * 15;

      // Core ring stroke
      ctx.globalAlpha = alpha * 0.9;
      ctx.strokeStyle = `hsl(${hue | 0}, ${sat}%, ${light | 0}%)`;
      ctx.lineWidth = ring.thickness;
      ctx.beginPath();
      ctx.arc(ring.x, ring.y, ring.radius, 0, Math.PI * 2);
      ctx.stroke();

      // Bloom halo — 3 progressively wider, lower-alpha layers
      if (glowAlpha > 0) {
        const bloomBase = alpha * glowAlpha;
        for (let layer = 1; layer <= 3; layer++) {
          ctx.globalAlpha = bloomBase * (0.35 / layer);
          ctx.lineWidth = ring.thickness + layer * 6;
          ctx.strokeStyle = `hsl(${hue | 0}, ${sat}%, ${(light + 10) | 0}%)`;
          ctx.beginPath();
          ctx.arc(ring.x, ring.y, ring.radius, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
    }

    // --- Update + draw particles ---
    for (let i = 0; i < MAX_PARTICLES; i++) {
      const p = this.particles[i];
      if (p.state === DEAD) continue;

      // Apply velocity and drag
      p.vx *= p.drag;
      p.vy *= p.drag;
      p.x += p.vx;
      p.y += p.vy;

      // Decay life
      p.life -= 0.018;

      if (p.life <= 0) {
        this.freeParticle(i);
        continue;
      }

      // Quadratic fade for smooth dimming
      const alpha = p.life * p.life;
      p.size *= 0.997;

      ctx.globalAlpha = alpha;
      ctx.fillStyle = `hsl(${p.hue | 0}, 85%, ${50 + alpha * 20 | 0}%)`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, Math.max(0.5, p.size), 0, Math.PI * 2);
      ctx.fill();
    }

    // --- Restore canvas state (composite mode, alpha) ---
    ctx.restore();
  }

  // --- Breathing vignette ---

  _drawVignette(bassEnergy) {
    const { ctx, canvas } = this;
    const w = canvas.width;
    const h = canvas.height;

    // Quantize bass to 20 levels for cache efficiency
    const bassQ = Math.round(bassEnergy * 20);

    // Rebuild gradient only when needed
    if (this._vignetteGrad === null || this._vignetteW !== w || this._vignetteH !== h || this._vignetteBassQ !== bassQ) {
      const cx = w / 2;
      const cy = h / 2;
      const outerR = Math.sqrt(cx * cx + cy * cy);

      // Inner stop oscillates with bass: 0.3 (quiet) to 0.6 (loud)
      const innerStop = 0.3 + (bassQ / 20) * 0.3;

      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, outerR);
      grad.addColorStop(0, 'rgba(0, 0, 0, 0)');
      grad.addColorStop(innerStop, 'rgba(0, 0, 0, 0)');
      grad.addColorStop(1, 'rgba(0, 0, 0, 0.6)');

      this._vignetteGrad = grad;
      this._vignetteW = w;
      this._vignetteH = h;
      this._vignetteBassQ = bassQ;
    }

    ctx.fillStyle = this._vignetteGrad;
    ctx.fillRect(0, 0, w, h);
  }

  // --- Reset ---

  clear() {
    super.clear();
    this.isFirstFrame = true;

    // Return all rings to pool
    this.ringFreeCount = MAX_RINGS;
    for (let i = 0; i < MAX_RINGS; i++) {
      this.rings[i].state = DEAD;
      this.ringFreeList[i] = i;
    }

    // Return all particles to pool
    this.particleFreeCount = MAX_PARTICLES;
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.particles[i].state = DEAD;
      this.particleFreeList[i] = i;
    }

    this.lastSpawnTime = 0;
    this.hueRotation = 0;

    // Invalidate vignette cache
    this._vignetteGrad = null;
    this._vignetteBassQ = -1;
  }
}

// --- Utility: extract hue from hex color ---

function hexToHue(hex) {
  if (!hex || hex.length < 7) return 0;
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  if (isNaN(r) || isNaN(g) || isNaN(b)) return 0;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  if (max === min) return 0;

  const d = max - min;
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
  else if (max === g) h = ((b - r) / d + 2) * 60;
  else h = ((r - g) / d + 4) * 60;

  return h;
}

// Register with the visualizer registry
VisualizerRegistry.register(ShockwaveBloom);

export { ShockwaveBloom };
export default ShockwaveBloom;
